import { useState, useEffect, useCallback } from "react";
import "./PasswordCard.scss";
import service from "../../services/Service.js";
import { Space, Button, notification, Modal, Input } from "antd";
import { SaveOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { withLocale } from "../../l10n/index.jsx";
import { getTokenInfo } from "../../api/token.js";
import { logout } from "../../api/user.js";
import { passwordStrengthValidation } from "../../utils/formValidation.jsx";
import { PASSWORD_UPDATED_AT, PASSWORD_EXPIRED_MODAL_SHOWN } from "../../utils/constants";
import { getPasswordStatus } from "../../utils/passwordStatus";
import withNavigation from "../hooks/withNavigation.jsx";

const PasswordCard = ({ lang, ident, editable, navigate }) => {
  // ===== 1. ESTADOS =====
  const [userData, setUserData] = useState({
    uuid: "",
    username: "",
  });

  const [passwords, setPasswords] = useState({
    password: "",
    repeatPassword: "",
  });

  const [errors, setErrors] = useState({
    password: "",
    strength: "",
  });

  // ===== 2. HELPERS =====
  const updateUserData = (field, value) =>
    setUserData((prev) => ({ ...prev, [field]: value }));
  const updatePasswords = (field, value) =>
    setPasswords((prev) => ({ ...prev, [field]: value }));
  const updateErrors = (field, value) =>
    setErrors((prev) => ({ ...prev, [field]: value }));

  // ===== 3. FUNCIONES PRINCIPALES =====
  const onInputChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "password": {
        updatePasswords("password", value);

        const newRepeatPasswordError =
          passwords.repeatPassword === "" || value === passwords.repeatPassword
            ? ""
            : lang.handle("¡Las contraseñas deben coincidir!");
        updateErrors("password", newRepeatPasswordError);

        const strengthError = passwordStrengthValidation(value);
        const strengthErrorMsg = strengthError.valid
          ? ""
          : lang.handle(strengthError.message);
        updateErrors("strength", strengthErrorMsg);
        break;
      }

      case "repeatPassword": {
        updatePasswords("repeatPassword", value);

        const newPasswordError =
          passwords.password === value
            ? ""
            : lang.handle("¡Las contraseñas deben coincidir!");
        updateErrors("password", newPasswordError);
        break;
      }

      default:
        break;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (passwords.password === "" || passwords.repeatPassword === "") {
      notification["error"]({
        message: lang.handle("La contraseña es un campo obligatorio"),
      });
      return;
    }

    const strengthResult = passwordStrengthValidation(passwords.password);
    if (!strengthResult.valid) {
      notification["error"]({
        message: lang.handle(strengthResult.message),
      });
      return;
    }

    if (!errors.password) {
      try {
        const res = await service.put("users/changepassword/" + userData.uuid, {
          password: passwords.password,
          repeatPassword: passwords.repeatPassword,
        });

        if (res.data.ok === false) {
          notification["error"]({
            message: res.data.message,
          });
          return;
        }

        notification["success"]({
          message: lang.handle("Contraseña actualizada correctamente"),
        });

        localStorage.removeItem(PASSWORD_UPDATED_AT);

        try {
          const resVPN = await service.put(
            "vpn/" + userData.username + "/changepassword",
            {
              password: passwords.password,
            },
          );

          if (resVPN.data.ok === false && resVPN.data.message) {
            notification["error"]({
              message: lang.handle(resVPN.data.message),
            });
            return;
          }

          notification["success"]({
            message: lang.handle("VPN actualizada correctamente"),
          });

          if (userData.uuid === getTokenInfo().uuid) {
            Modal.confirm({
              title: lang.handle(
                "Su sesión se cerrará para aplicar los cambios",
              ),
              content: lang.handle("Inicie sesión de nuevo"),
              icon: <ExclamationCircleOutlined />,
              cancelButtonProps: { disabled: true, style: { display: "none" } },
              onOk: async () => {
                const result = await logout();
                if (result) navigate(0);
              },
            });
          } else {
            navigate("/users/list");
          }
        } catch (err) {
          console.error("Error VPN:", err);
        }
      } catch (err) {
        console.error("Error cambio contraseña:", err);
      }
    } else {
      if (errors.password) {
        notification["error"]({
          message: lang.handle("Las contraseñas deben coincidir"),
        });
      }
    }
  };

  const Pfetch = useCallback(async () => {
    const { expired } = getPasswordStatus();
    const alreadyShown =
      sessionStorage.getItem(PASSWORD_EXPIRED_MODAL_SHOWN) === "true";

    if (expired && !alreadyShown) {
      Modal.info({
        title: lang.handle("Su contraseña ha caducado"),
        content: lang.handle(
          "Han pasado más de 6 meses desde el último cambio. Por motivos de seguridad debe establecer una nueva contraseña antes de continuar.",
        ),
        okText: lang.handle("Entendido"),
        icon: <ExclamationCircleOutlined />,
      });
      sessionStorage.setItem(PASSWORD_EXPIRED_MODAL_SHOWN, "true");
    }

    try {
      const res = await service.get("users/" + ident);

      if (res.data.ok === false) {
        notification["error"]({
          message: res.data.message,
        });
        return;
      }

      if (!res.data.data) {
        return;
      }

      updateUserData("uuid", res.data.data[0].uuid);
      updateUserData("username", res.data.data[0].username);
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
  }, [ident, lang]);

  // ===== 4. EFFECTS =====
  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  // ===== 5. RENDER =====
  return (
    <div className="card card-body">
      <h4>
        {lang.handle("Cambio de contraseña usuario") + ":"} {userData.username}
      </h4>
      <form onSubmit={onSubmit}>
        <div className="form-group row">
          <label
            htmlFor="password"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle("Contraseña")}
          </label>
          <div className="col-sm-10">
            <Input.Password
              className="form-control"
              id="password"
              onChange={onInputChange}
              name="password"
              value={passwords.password}
            />
            {errors.strength.length > 0 && (
              <span className="boardFormError">{errors.strength}</span>
            )}
          </div>
        </div>

        <div className="form-group row">
          <label
            htmlFor="repeatPassword"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle("Repetir contraseña")}
          </label>
          <div className="col-sm-10">
            <Input.Password
              className="form-control"
              id="repeatPassword"
              onChange={onInputChange}
              name="repeatPassword"
              value={passwords.repeatPassword}
            />
            {errors.password.length > 0 && (
              <span className="boardFormError">{errors.password}</span>
            )}
          </div>
        </div>

        {editable && (
          <div className="button-edit-user">
            <Space>
              <Button
                id="button-save"
                htmlType="submit"
                type="primary"
                icon={<SaveOutlined />}
              >
                {lang.handle("Guardar")}
              </Button>
            </Space>
          </div>
        )}
      </form>
    </div>
  );
};

export default withLocale(withNavigation(PasswordCard));
